import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {AttachmentObject, constants, RefModelStoreService} from '@q9elements/ui-kit/common';
import {forEach, get, isEqual, map, set, zip} from 'lodash';

import {GridDataSourceService} from '../../custom-grid/services/grid-data-source.service';
import {TreeUpdateObject} from '../../models/rme/tree-update-object';
import {AttachmentsService} from '../../services/dao/attachments.service';

const TAGS = 'tags';
const AVERAGE_TAG_WIDTH = 90;

@Injectable()
export class GridUpdateService {
  private gdprStatuses: any;

  constructor(
    private rmeStore: RefModelStoreService,
    private customGridDataSourceService: GridDataSourceService,
    private attachmentsService: AttachmentsService,
    private translate: TranslateService
  ) {
    this.initGDPRStatuses();
  }

  private initGDPRStatuses(): void {
    this.gdprStatuses = {};
    map(constants.gdprOptions.gdprStatuses, item => {
      this.gdprStatuses[item.key] = item.value;
    });
  }

  public updateAttachments(attachmentsObj: AttachmentObject, fieldToSearch: string): void {
    const selectedNode = this.rmeStore.selectedNode;

    if (!attachmentsObj?.loading) {
      const items = this.customGridDataSourceService.getItems();
      const item = items.find(node => node[fieldToSearch] === selectedNode._id);
      const links = this.attachmentsService.getLinksNumber(attachmentsObj);

      if (item && item.links !== links) {
        item.links = links;

        this.customGridDataSourceService.updateItem(item, fieldToSearch);
      }
    }
  }

  public updateNode(data: TreeUpdateObject, fieldToSearch: string, tagsField = TAGS): void {
    const {nodeId, property} = data;
    const items = this.customGridDataSourceService.getItems();
    const item = items.find(node => node[fieldToSearch] === nodeId);
    const isItemsEqual = this.compareItemsProperty(
      item,
      property === TAGS ? tagsField : property,
      data.value
    );

    if (property === 'gdprStatus' && data.value) {
      data.value = this.translate.instant(this.gdprStatuses[data.value]);
    }

    if (item && !isItemsEqual) {
      this.updateItemProperty(item, data.value, property === TAGS ? tagsField : property);
      this.customGridDataSourceService.updateItem(item, fieldToSearch);
    }
  }

  public updateNodes(
    nodes: any[],
    fieldToSearch: string,
    pathsToUpdatedValues: string[],
    pathsToGridValues?: string[]
  ): void {
    const valuesMap = new Map<string, string[]>();

    if (!pathsToGridValues) {
      pathsToGridValues = [...pathsToUpdatedValues];
    }

    const pathsZip = zip(pathsToUpdatedValues, pathsToGridValues);

    forEach(nodes, node => valuesMap.set(node._id, node));

    const items = this.customGridDataSourceService
      .getItems()
      .filter(item => valuesMap.has(item[fieldToSearch]));

    for (const item of items) {
      forEach(pathsZip, path =>
        set(item, path[1], get(valuesMap.get(item[fieldToSearch]), path[0]))
      );
    }

    valuesMap.clear();

    this.customGridDataSourceService.updateItems(items, fieldToSearch);
  }

  public calculateTagsColumnWidth(nodes, tagsPath = 'tags'): number {
    const maxTagsInUpdatedNodes = this.calculateMaxTagsInNodes(nodes, tagsPath);
    const maxTagsInGrid = this.calculateMaxTagsInNodes(
      this.customGridDataSourceService.getItems(),
      tagsPath
    );

    return maxTagsInGrid > maxTagsInUpdatedNodes
      ? maxTagsInGrid * AVERAGE_TAG_WIDTH
      : maxTagsInUpdatedNodes * AVERAGE_TAG_WIDTH;
  }

  private updateItemProperty(item, value, property) {
    set(item, property, value);
    return item;
  }

  private compareItemsProperty(item, property, value) {
    const itemValue = get(item, property);

    if (Array.isArray(item) && Array.isArray(value)) {
      return itemValue.every(v => value.includes(v));
    }

    return isEqual(itemValue, value);
  }

  private calculateMaxTagsInNodes(nodes, tagsPath) {
    return nodes.reduce((total, node) => {
      const tagsNumber = get(node, tagsPath, []).length;
      return tagsNumber > total ? tagsNumber : total;
    }, 0);
  }
}
